'use client';

import { type PaymentMethod, PaymentMethodCode } from '@ahm/api-wrappers-core';
import { rem } from '@mantine/core';
import { closeModal, type ContextModalProps } from '@mantine/modals';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import QrCodeInfo from '../qrcode-info';
import { ModalName, showModal } from '.';
import { useGetPaymentTransactionStatus } from '@/features/checkout/api/payment';
import { useCheckoutStore } from '@/features/checkout/store';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useDetectModalStore } from '@/stores';

export type ModalCheckoutProps = ContextModalProps<{
  title: string | ReactNode;
  amount: number;
  data: any;
  paymentMethod: PaymentMethod | null;
  requestBody?: any;
}>;

export enum PayStatus {
  confirming = 'confirming',
  failed = 'failed',
  success = 'success',
}

export default function ModalCheckout({ id, innerProps }: ModalCheckoutProps) {
  const { title, amount, data, requestBody } = innerProps;
  const isMobile = useIsMobile();
  const [isPaid, setIsPaid] = useState<PayStatus | null>(null);
  const paymentMethod =
    innerProps.paymentMethod ?? useCheckoutStore.use.checkoutPaymentMethod();
  const { data: transactionStatus } = useGetPaymentTransactionStatus(
    data.payment_provider_log_id as string,
    {
      enabled:
        !!data.payment_provider_log_id &&
        (isPaid === null || isPaid === PayStatus.confirming),
      refetchInterval: 5000,
    }
  );
  useEffect(() => {
    if (
      transactionStatus?.status &&
      transactionStatus.status !== isPaid &&
      transactionStatus?.status !== PayStatus.confirming
    ) {
      setIsPaid(transactionStatus.status as PayStatus);

      showModal(
        ModalName.CheckoutResult,
        {
          transaction: { ...data, status: transactionStatus.status },
          paymentMethod,
          requestBody,
        },
        {
          p: 0,
          w: rem(410),
          styles: {
            body: {
              position: 'relative',
              padding: 0,
            },
          },
          closeOnEscape: false,
          fullScreen: isMobile,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid, transactionStatus]);

  const qrcodeValue = useMemo(() => {
    if (!data.data && data.qr_code_url) {
      return data.qr_code_url;
    }

    return paymentMethod?.code !== PaymentMethodCode.Viettelpay.toLowerCase()
      ? data.data
      : JSON.stringify(data.data);
  }, [data.data, data.qr_code_url, paymentMethod?.code]);

  const setDetectModalId = useDetectModalStore.use.setDetectModalId();
  const clearDetectModalId = useDetectModalStore.use.clearDetectModalId();

  const handleCloseModal = () => {
    // closeAllModals();
    closeModal(id);
  };

  useEffect(() => {
    setDetectModalId(id);
    return () => {
      clearDetectModalId();
    };
  }, [id, setDetectModalId, clearDetectModalId]);

  return (
    <QrCodeInfo
      title={title}
      amount={amount}
      paymentMethod={paymentMethod}
      qrCodeValue={qrcodeValue}
      onClose={handleCloseModal}
    />
  );
}
